import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface AuthFormProps {
  mode: 'login' | 'register';
  onToggleMode: () => void;
}

interface Notification {
  type: 'success' | 'error';
  message: string;
}

export default function AuthForm({ mode, onToggleMode }: AuthFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const { login, register } = useAuthStore();

  useEffect(() => {
    let timer: number;
    if (notification) {
      timer = window.setTimeout(() => {
        setNotification(null);
      }, 2000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [notification]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setUsername(value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setPassword(value);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setConfirmPassword(value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setEmail(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotification(null);

    if (!username || !password) {
      setNotification({
        type: 'error',
        message: '请输入用户名和密码'
      });
      return;
    }

    if (mode === 'register') {
      if (!email) {
        setNotification({
          type: 'error',
          message: '请输入邮箱地址'
        });
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setNotification({
          type: 'error',
          message: '请输入有效的邮箱地址'
        });
        return;
      }

      if (!confirmPassword) {
        setNotification({
          type: 'error',
          message: '请确认密码'
        });
        return;
      }

      if (password !== confirmPassword) {
        setNotification({
          type: 'error',
          message: '两次输入的密码不一致'
        });
        return;
      }
    }

    setSubmitting(true);

    try {
      if (mode === 'login') {
        const success = await login(username, password);
        if (success) {
          // 登录成功，跳转到之前的页面，如果之前是登录/注册页则跳转到首页
          const from = (location.state as any)?.from?.pathname;
          if (from && from !== '/login') {
            navigate(from, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } else {
          setNotification({
            type: 'error',
            message: '用户名或密码错误'
          });
        }
      } else {
        const success = await register(username, email, password, confirmPassword);
        if (success) {
          setNotification({
            type: 'success',
            message: '注册成功'
          });
          // 注册成功后，跳转到之前的页面或首页
          setTimeout(() => {
            const from = (location.state as any)?.from?.pathname;
            if (from && from !== '/login') {
              navigate(from, { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }, 1000);
        } else {
          setNotification({
            type: 'error',
            message: '注册失败，用户名或邮箱已被使用'
          });
        }
      }
    } catch (err) {
      setNotification({
        type: 'error',
        message: '操作失败，请稍后重试'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              用户名
            </label>
            <input
              id="username"
              type="text"
              required
              className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
              placeholder="请输入方便书院识别的用户名"
              value={username}
              onChange={handleUsernameChange}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              密码
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="block w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                placeholder={mode === 'login' ? '请输入密码' : '请输入易记密码'}
                value={password}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  邮箱
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                  placeholder="请输入邮箱地址"
                  value={email}
                  onChange={handleEmailChange}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  确认密码
                </label>
                <div className="relative mt-1">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="block w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500"
                    placeholder="请再次输入密码"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {notification && (
          <div
            className={clsx(
              'text-sm text-center py-2 px-4 rounded-lg transition-opacity duration-200',
              notification.type === 'success' 
                ? 'bg-green-50 text-green-600'
                : 'bg-red-50 text-red-600'
            )}
          >
            {notification.message}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex justify-center items-center space-x-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{submitting ? (mode === 'login' ? '登录中...' : '注册中...') : (mode === 'login' ? '登录' : '注册')}</span>
        </button>

        <button
          type="button"
          onClick={() => {
            const from = (location.state as any)?.from?.pathname;
            if (from && from !== '/login') {
              navigate(from, { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }}
          className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
        >
          暂不登录
        </button>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onToggleMode}
            className="text-sm text-red-600 hover:text-red-700"
          >
            {mode === 'login' ? '没有账号？立即注册' : '已有账号？立即登录'}
          </button>

          {mode === 'login' && (
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-gray-600 hover:text-gray-700"
            >
              忘记密码？
            </button>
          )}
        </div>

        <div className="text-center text-xs text-gray-500 mt-8 mb-12">
          <p>Copyright©2024-2034 梅花易道书院 版权所有</p>
        </div>
      </form>
    </div>
  );
}